"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Activity, Binoculars, MonitorPlay, UserCheck, Compass, ArrowRight } from "lucide-react";
const servicesData = [{
  title: "Player Skill Development",
  icon: Activity,
  image: "https://dbhockeyadvancement.com/wp-content/uploads/2026/03/IMG_8819.jpg?v=zofu093joke",
  p1: "Our Player Skill Development program is designed to push athletes beyond their current limits. We focus on the micro-details of skating, puck handling, and shooting that separate good players from elite prospects.",
  p2: "Through rigorous, high-intensity on-ice sessions, we build the muscle memory and hockey IQ required to execute at the next level. Every drill is purposeful, translating directly to in-game success and noticeable performance improvements."
}, {
  title: "Scouting and Recruitment Assistance",
  icon: Binoculars,
  image: "https://dbhockeyadvancement.com/wp-content/uploads/2026/03/IMG_8363.jpg",
  p1: "Navigating the complex world of junior and college hockey scouting requires experience and connections. We provide comprehensive Scouting and Recruitment Assistance to ensure our players are seen by the right decision-makers.",
  p2: "We leverage our extensive network of coaches and scouts across North America to advocate for our athletes. From initial contact to final commitments, we guide families through every step of the recruitment process with transparency and strategic planning."
}, {
  title: "Video Analysis",
  icon: MonitorPlay,
  image: "https://dbhockeyadvancement.com/wp-content/uploads/2026/03/IMG_7409.jpg?v=9kabaskbboa",
  p1: "Video Analysis is a critical tool for modern player development. We break down game tape to identify areas of strength and opportunities for improvement, providing objective feedback that accelerates learning and tactical understanding.",
  p2: "Our detailed video sessions help players understand positional play, decision-making under pressure, and systemic execution. We also create personalized, high-impact highlight packages that effectively showcase a player's true potential to prospective teams."
}, {
  title: "Player Profile Optimization",
  icon: UserCheck,
  image: "https://dbhockeyadvancement.com/wp-content/uploads/2026/03/IMG_1353.jpg?v=h9fyzuk6dxw",
  p1: "A player's online presence is often their first impression on a scout. Our Player Profile Optimization service ensures that Elite Prospects pages, social media, and personal websites present a professional and compelling narrative.",
  p2: "We assist in curating statistics, academic achievements, and character references into a cohesive package. A polished, easily accessible profile significantly increases the likelihood of catching a recruiter's eye in a crowded and competitive landscape."
}, {
  title: "College and Junior Hockey Pathway Guidance",
  icon: Compass,
  image: "https://dbhockeyadvancement.com/wp-content/uploads/2026/03/IMG_0861.jpeg?v=s0rtis89us",
  p1: "The journey to college or major junior hockey is unique for every athlete. We offer personalized Pathway Guidance to map out the most effective route for each player's specific goals, academic standing, and athletic abilities.",
  p2: "Whether it's navigating the USHL, NAHL, CHL, or NCAA eligibility requirements, we provide clear, actionable advice. We help families make informed decisions about drafts, tenders, showcase attendances, and ultimate school or team commitments."
}];
function ServiceRow({
  service,
  index
}: {
  service: typeof servicesData[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const isEven = index % 2 === 0;
  const Icon = service.icon;
  return <div ref={ref} data-index={index} className={`flex flex-col gap-8 lg:gap-16 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} ${isEven ? "lg:flex-row" : "lg:flex-row-reverse"}`}>
      <div className="w-full lg:w-1/2 relative group">
        <div className="absolute -inset-2 bg-primary/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-primary z-10 transition-transform duration-500 group-hover:-translate-x-2 group-hover:-translate-y-2"></div>
        <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-primary z-10 transition-transform duration-500 group-hover:translate-x-2 group-hover:translate-y-2"></div>
        
        <div className="relative aspect-[4/3] overflow-hidden bg-muted border border-border/50 [clip-path:polygon(0_0,100%_0,100%_calc(100%-2rem),calc(100%-2rem)_100%,0_100%)]">
          <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 grayscale hover:grayscale-0" />
          <div className="absolute inset-0 bg-gradient-to-tr from-background/80 via-transparent to-transparent opacity-60"></div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex flex-col space-y-6">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center bg-background/50 backdrop-blur-md border border-border/50 [clip-path:polygon(0_0,100%_0,100%_calc(100%-10px),calc(100%-10px)_100%,0_100%)]">
            <Icon className="w-6 h-6 text-primary" />
          </div>
          <h3 className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-foreground">
            {service.title}
          </h3>
        </div>
        
        <div className="space-y-4 text-muted-foreground leading-relaxed border-l-2 border-primary/30 pl-6">
          <p>{service.p1}</p>
          <p>{service.p2}</p>
        </div>
      </div>
    </div>;
}
export default function AdvancementServices() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="advancement-services" className="dark bg-background text-foreground py-24 md:py-32 overflow-hidden relative">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        <div ref={headerRef} className={`max-w-3xl mb-20 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 border border-primary/30 bg-primary/10 text-primary text-sm font-mono uppercase tracking-wider [clip-path:polygon(0_0,100%_0,100%_calc(100%-8px),calc(100%-8px)_100%,0_100%)]">
            <span className="w-2 h-2 bg-primary rounded-none animate-pulse"></span>
            Core Capabilities
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tighter uppercase mb-6">
            Advancement <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/50">Services</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            A comprehensive suite of development and exposure tools engineered to elevate your game and secure your future in elite hockey.
          </p>
        </div>

        <div className="space-y-24 md:space-y-32">
          {servicesData.map((service, index) => <ServiceRow key={service.title} service={service} index={index} />)}
        </div>

        <div className="mt-32 pt-16 border-t border-border/50 flex flex-col items-center text-center">
          <h3 className="text-2xl font-bold uppercase mb-6">Ready to start your advancement journey?</h3>
          <Button asChild size="lg" className="rounded-none group relative overflow-hidden bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/PlayerInquiryPage">
              <span className="relative z-10 flex items-center gap-2 font-semibold tracking-wide uppercase">
                Submit Player Inquiry
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
              <div className="absolute inset-0 h-full w-full bg-white/20 -skew-x-12 -translate-x-full group-hover:animate-[shimmer_1s_forwards]"></div>
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}